<script setup lang="ts">
import PageBreadcrumb from '@/components/common/PageBreadcrumb.vue';
import { ref, computed, onMounted } from 'vue';
import PriceDataService from '@/services/PriceDataService';
import PriceCategoryService from '@/services/PriceCategoryService';
import type { Price } from '@/types';
import type { PriceCategory } from '@/services/PriceCategoryService';
import { Table, TableHeader, TableBody, TableRow, TableCell } from '@/components/ui/table';
import { TableCard, TableEmptyState, TableFilterDropdown } from '@/components/common/table';

// State
const prices = ref<Price[]>([]);
const loading = ref(true);
const error = ref<string | null>(null);

// Price form state
const showModal = ref(false);
const editMode = ref(false);
const duplicateMode = ref(false);
const formSubmitting = ref(false);
const priceForm = ref<Partial<Price>>({
    name: '',
    key: '',
    category: 'sro',
    subcategory: '',
    description: '',
    normalPrice: 0,
    salePrice: undefined,
    isActive: true,
    priority: 0,
});

// Delete confirmation state
const showDeleteModal = ref(false);
const priceToDelete = ref<Price | null>(null);
const deleteSubmitting = ref(false);

// Category filtering
const selectedCategory = ref('all');
const selectedCategoryName = ref('');
const priceCategories = ref<PriceCategory[]>([]);
const categories = computed(() => {
    return [
        { value: 'all', label: 'Všetky kategórie' },
        ...priceCategories.value.map(category => ({
            value: category.name,
            label: category.name.charAt(0).toUpperCase() + category.name.slice(1)
        }))
    ];
});

// Available subcategories for selected category
const availableSubcategories = computed(() => {
    if (!selectedCategoryName.value) {
        return [];
    }

    const category = priceCategories.value.find(cat => cat.name === selectedCategoryName.value);
    return category?.subcategories || [];
});

// Computed property for filtered prices
const filteredPrices = computed(() => {
    if (selectedCategory.value === 'all') {
        return prices.value;
    }
    return prices.value.filter(price => price.category === selectedCategory.value);
});

// Fetch all prices and categories on component mount
onMounted(async () => {
    await Promise.all([
        fetchPrices(),
        fetchCategories()
    ]);
});

// Methods
async function fetchPrices() {
    loading.value = true;
    error.value = null;

    try {
        const response = await PriceDataService.getAllPrices();
        const data = response.data.data || response.data;
        prices.value = Array.isArray(data) ? data : [];
    } catch (err) {
        console.error('Error fetching prices:', err);
        error.value = 'Nastala chyba pri načítaní cien. Skúste to prosím neskôr.';
    } finally {
        loading.value = false;
    }
}

// Fetch all categories
async function fetchCategories() {
    try {
        const response = await PriceCategoryService.getAllCategories();
        const data = response.data.data || response.data;
        priceCategories.value = Array.isArray(data) ? data : [];
    } catch (err) {
        console.error('Error loading categories:', err);
        // Don't set error state to avoid affecting the whole UI
    }
}

// Format price in EUR
function formatPrice(price: number): string {
    return new Intl.NumberFormat('sk-SK', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(price);
}

// Add new price handler
function handleAddPrice() {
    editMode.value = false;
    duplicateMode.value = false;
    resetPriceForm();
    showModal.value = true;
}

// Edit price handler
function handleEditPrice(price: Price) {
    editMode.value = true;
    duplicateMode.value = false;
    priceForm.value = { ...price };
    // Set the selected category name for the select dropdown
    selectedCategoryName.value = price.category || '';
    showModal.value = true;

    // Debug log to check if category is properly set
    console.log('Editing price:', price);
    console.log('Category from price:', price.category);
    console.log('Selected category name:', selectedCategoryName.value);
}

// Duplicate price handler
function handleDuplicatePrice(price: Price) {
    editMode.value = false; // Set to create mode (not edit)
    duplicateMode.value = true; // Set duplicate mode

    priceForm.value = {
        ...price,
        key: price.key, // Keep original key (user must manually change it)
        id: undefined // Remove the ID so it creates a new record
    };

    // Set the selected category name for the select dropdown
    selectedCategoryName.value = price.category || '';
    showModal.value = true;

    console.log('Duplicating price:', price);
    console.log('Original key preserved:', priceForm.value.key);
    console.log('Form data for duplicate:', priceForm.value);
}

// Handle category selection change
function onCategoryChange() {
    priceForm.value.category = selectedCategoryName.value;
    // Reset subcategory when category changes
    priceForm.value.subcategory = '';
}

// Toggle price active status
async function handleToggleStatus(price: Price) {
    try {
        await PriceDataService.togglePriceStatus(price.id, !price.isActive);
        await fetchPrices();
    } catch (err) {
        console.error('Error toggling price status:', err);
        alert('Nastala chyba pri zmene stavu ceny.');
    }
}

// Delete price handler
function handleDeletePrice(price: Price) {
    priceToDelete.value = price;
    showDeleteModal.value = true;
}

// Confirm delete action
async function confirmDelete() {
    if (!priceToDelete.value) return;

    deleteSubmitting.value = true;

    try {
        await PriceDataService.deletePrice(priceToDelete.value.id);
        showDeleteModal.value = false;
        await fetchPrices();
    } catch (err) {
        console.error('Error deleting price:', err);
        alert('Nastala chyba pri vymazávaní ceny.');
    } finally {
        deleteSubmitting.value = false;
        priceToDelete.value = null;
    }
}

// Reset form to default values
function resetPriceForm() {
    priceForm.value = {
        name: '',
        key: '',
        category: '',
        subcategory: '',
        description: '',
        normalPrice: 0,
        salePrice: undefined as number | undefined,
        isActive: true,
        priority: 0,
    };
    selectedCategoryName.value = '';
    duplicateMode.value = false;
}

// Submit price form (create or update)
async function submitPriceForm() {
    formSubmitting.value = true;

    try {
        if (editMode.value && priceForm.value.id) {
            await PriceDataService.updatePrice(priceForm.value.id, priceForm.value);
        } else {
            await PriceDataService.createPrice(priceForm.value);
        }

        showModal.value = false;
        await fetchPrices();
    } catch (err) {
        console.error('Error saving price:', err);

        // Handle specific validation errors
        if (err && typeof err === 'object' && 'response' in err) {
            const response = (err as { response?: { status?: number; data?: { error?: unknown } } }).response;
            if (response?.status === 422) {
                const errors = response.data?.error;
                if (errors && typeof errors === 'object' && 'key' in errors) {
                    const keyErrors = (errors as { key?: string[] }).key;
                    if (keyErrors && keyErrors[0]) {
                        alert(`Chyba: ${keyErrors[0]}`);
                        return;
                    }
                }
                alert('Chyba validácie údajov. Skontrolujte všetky polia.');
            } else if (response?.data && typeof response.data === 'object' && 'error' in response.data) {
                const errorMessage = (response.data as { error?: string }).error;
                if (errorMessage) {
                    alert(`Chyba: ${errorMessage}`);
                    return;
                }
            }
        }
        alert('Nastala chyba pri ukladaní ceny.');
    } finally {
        formSubmitting.value = false;
    }
}
</script>

<template>
  <div class="px-4 sm:px-6 lg:px-8 py-8">
    <div class="sm:flex sm:items-center sm:justify-between">
      <div class="sm:flex-auto">
        <PageBreadcrumb page-title="Správa cien" />
        <p class="mt-2 text-sm text-gray-700 dark:text-gray-300">Správa cienníka a kategorizácia produktov a služieb</p>
      </div>
      <div class="mt-4 sm:mt-0 sm:ml-auto">
        <button @click="handleAddPrice" class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500">
          <svg class="-ml-1 mr-2 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          Pridať cenu
        </button>
      </div>
    </div>
    <TableCard title="Správa cien">
      <template #controls>
        <div class="flex w-full items-center gap-3 sm:w-auto">
          <TableFilterDropdown
            :options="categories"
            label="Kategória"
            v-model="selectedCategory"
          />
          <button
            type="button"
            class="shrink-0 px-4 py-2 bg-brand-600 text-white rounded-md"
            @click="handleAddPrice"
          >
            Pridať cenu
          </button>
        </div>
      </template>
      <div class="max-w-full overflow-x-auto min-w-0">
        <Table class="min-w-full divide-y divide-gray-100 dark:divide-gray-800">
          <TableHeader
            class="border-y border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900"
          >
            <TableRow>
              <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">Názov</TableCell>
              <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">Kód</TableCell>
              <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">Kategória</TableCell>
              <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">Podkategória</TableCell>
              <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">Popis</TableCell>
              <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">Cena</TableCell>
              <TableCell is-header class="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400">Aktívne</TableCell>
              <TableCell is-header class="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400">Akcie</TableCell>
            </TableRow>
          </TableHeader>
          <TableBody class="divide-y divide-gray-100 dark:divide-gray-800">
            <TableEmptyState
              v-if="filteredPrices.length === 0"
              title="Žiadne ceny"
              hint="Skúste upraviť filter kategórie."
              colspan="8"
            />
            <template v-for="price in filteredPrices" :key="price.id">
              <TableRow class="hover:bg-gray-50 dark:hover:bg-white/5">
                <TableCell class="px-6 py-3 text-left text-sm font-medium text-gray-800 dark:text-white">{{ price.name }}</TableCell>
                <TableCell class="px-6 py-3 text-left text-sm text-gray-500 dark:text-gray-400">{{ price.key }}</TableCell>
                <TableCell class="px-6 py-3 text-left text-sm text-gray-500 dark:text-gray-400">{{ price.category }}</TableCell>
                <TableCell class="px-6 py-3 text-left text-sm text-gray-500 dark:text-gray-400">{{ price.subcategory }}</TableCell>
                <TableCell class="px-6 py-3 text-left text-sm text-gray-500 dark:text-gray-400">{{ price.description }}</TableCell>
                <TableCell class="px-6 py-3 text-left text-sm text-gray-500 dark:text-gray-400">{{ formatPrice(price.normalPrice) }}</TableCell>
                <TableCell class="px-6 py-3 text-center text-sm">
                  <span :class="price.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium">
                    {{ price.isActive ? 'Áno' : 'Nie' }}
                  </span>
                </TableCell>
                <TableCell class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-white/90">
                  <button @click="() => handleToggleStatus(price)" class="px-2 py-1 text-sm text-green-600 hover:text-green-700 dark:text-green-500 dark:hover:text-green-400">{{ price.isActive ? 'Skryť' : 'Zobraziť' }}</button>
                  <button @click="handleEditPrice(price)" class="ml-2 px-2 py-1 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-500 dark:hover:text-blue-400">Upraviť</button>
                  <button @click="handleDuplicatePrice(price)" class="ml-2 px-2 py-1 text-sm text-indigo-600 hover:text-indigo-700 dark:text-indigo-500 dark:hover:text-indigo-400">Kopírovať</button>
                  <button @click="handleDeletePrice(price)" class="ml-2 px-2 py-1 text-sm text-red-600 hover:text-red-700 dark:text-red-500 dark:hover:text-red-400">Vymazať</button>
                </TableCell>
              </TableRow>
            </template>
          </TableBody>
        </Table>
      </div>
      <template #footer>
      </template>
    </TableCard>

    <!-- Price Form Modal -->
    <div v-if="showModal" class="fixed inset-0 bg-black/30 flex items-center justify-center z-50 dark:bg-white/3">
      <div class="bg-white p-6 rounded-sm shadow-lg w-full max-w-md dark:bg-white/3">
        <h2 class="text-xl font-bold mb-4">{{ editMode ? 'Upraviť cenu' : 'Pridať cenu' }}</h2>
        <form @submit.prevent="submitPriceForm" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Názov ceny</label>
            <input type="text" v-model="priceForm.name" class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-brand-500 focus:border-brand-500 sm:text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Kód ceny</label>
            <input type="text" v-model="priceForm.key" class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-brand-500 focus:border-brand-500 sm:text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Kategória</label>
            <select v-model="priceForm.category" class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-brand-500 focus:border-brand-500 sm:text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white">
              <option value="sro">S.R.O.</option>
              <option value="oses">OSVČ</option>
              <option value="vz">Verejná služba</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Podkategória</label>
            <input type="text" v-model="priceForm.subcategory" class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-brand-500 focus:border-brand-500 sm:text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Popis</label>
            <textarea v-model="priceForm.description" rows="3" class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-brand-500 focus:border-brand-500 sm:text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white"></textarea>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Cena (EUR)</label>
            <input type="number" step="0.01" v-model="priceForm.normalPrice" class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-brand-500 focus:border-brand-500 sm:text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white" />
          </div>
          <div class="flex items-end gap-2">
            <input type="checkbox" v-model="priceForm.isActive" class="rounded-sm border-gray-300 text-brand-600 focus:ring-brand-500 dark:bg-gray-800 dark:border-gray-700" />
            <label class="ml-2 text-sm text-gray-700 dark:text-gray-300">Aktívna</label>
          </div>
          <div class="flex justify-end pt-4">
            <button type="button" @click="showModal = false" class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-transparent rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700">Zrušiť</button>
            <button type="submit" class="ml-3 px-4 py-2 text-sm font-medium text-white bg-brand-600 border border-transparent rounded-md hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500" :disabled="formSubmitting">
              {{ formSubmitting ? 'Ukladám...' : 'Uložiť' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="fixed inset-0 bg-black/30 flex items-center justify-center z-50 dark:bg-white/3">
      <div class="bg-white p-6 rounded-sm shadow-lg w-full max-w-md dark:bg-white/3">
        <h2 class="text-lg font-semibold text-gray-800 dark:text-white">Vymazať cenu</h2>
        <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">Naozaj chcete vymazať cenu <strong>{{ priceToDelete?.name }}</strong>?</p>
        <div class="flex justify-end pt-4">
          <button @click="showDeleteModal = false" class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-transparent rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700">Zrušiť</button>
          <button @click="confirmDelete" class="ml-3 px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" :disabled="deleteSubmitting">{{ deleteSubmitting ? 'Mazanie...' : 'Vymazať' }}</button>
        </div>
      </div>
    </div>
  </template>

<style scoped>
/* Add any component-specific styles here */
</style>
